import jQuery from "jquery";

import type { ISnippetCompletionProviderOptions } from "../Header.ts";
import { recordValues } from "@de-xima/xima-common-js-lang";

import { getSnippets } from "./Snippets.ts";

const NoOpCompletionItemProvider: import("monaco-editor").languages.CompletionItemProvider = {
  provideCompletionItems() {
    return {
      suggestions: [],
    };
  },
};

/**
 * @param positionedWord A word to convert into a range.
 * @param lineNumber Line where the word is located.
 * @returns The range for the word.
 */
function createRangeForWord(
  positionedWord: import("monaco-editor").editor.IWordAtPosition,
  lineNumber: number,
): import("monaco-editor").Range {
  return new monaco.Range(lineNumber, positionedWord.startColumn, lineNumber, positionedWord.endColumn);
}

/**
 * Creates a completion item provider that suggests the given snippets.
 * @param options Options for the provider. Must contain at least a value for
 * `snippetNames`.
 * @returns A completion item provider for the given snippets.
 */
export function createSnippetCompletionProvider(
  options: ISnippetCompletionProviderOptions,
): import("monaco-editor").languages.CompletionItemProvider {
  if (typeof monaco !== "object") {
    console.warn("Cannot add snippets, no monaco editor found.");
    return NoOpCompletionItemProvider;
  }

  const suggestions = [] as Omit<import("monaco-editor").languages.CompletionItem, "range">[];

  for (const snippetName of options.snippetNames) {
    const data = getSnippets(snippetName);
    if (data === undefined) {
      console.warn("No such snippet library found:", snippetName);
      continue;
    }
    for (const snippet of recordValues(data)) {
      suggestions.push({
        documentation: snippet.description ?? "",
        insertText: snippet.body.join("\n"),
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        kind: monaco.languages.CompletionItemKind.Snippet,
        label: snippet.prefix ?? "",
      });
    }
  }

  if (suggestions.length === 0) {
    return NoOpCompletionItemProvider;
  }

  return {
    provideCompletionItems(model, position) {
      // Current word to autocomplete, eg. "tf3"
      const positionedWord = model.getWordUntilPosition(position);
      const range = createRangeForWord(positionedWord, position.lineNumber);
      return {
        suggestions: suggestions
          .filter((suggestion) => suggestion.insertText.indexOf(positionedWord.word) >= 0)
          .map((suggestion) => {
            return jQuery.extend({}, suggestion, {
              range: range,
            });
          }),
      };
    },
    resolveCompletionItem(item) {
      const text = item.insertText ?? "";
      const newText = options.textReplacer?.(text) ?? text;
      return Object.assign({}, item, {
        insertText: newText,
      });
    },
  };
}
